import { defineStore } from "pinia"
import { ref, computed } from "vue"
import { useRouter } from "vue-router"
import { formatAccountPermissions } from "@/api/account/utils"
import { useJourneyStore } from "@/stores/AutomatedMarketingGroup/journeys"
import { reset401Flag } from "@/composables/useRequest"

export const useUserStore = defineStore("user", () => {
    const router = useRouter()

    const userInfo = ref(JSON.parse(localStorage.getItem("userInfo")))
    const userPermissions = ref(JSON.parse(localStorage.getItem("userPermissions")))
    const userToken = ref(JSON.parse(localStorage.getItem("userToken")))

    const formatUserPermissions = computed(() =>
        formatAccountPermissions(userPermissions.value)
    )
    const formatUserInfo = computed(() => userInfo.value)
    const formatUserToken = computed(() => userToken.value?.access)
    const formatUserRefreshToken = computed(() => userToken.value?.refresh)

    /**
     * 存取用戶資料，附帶處理 localStorage
     * @param {object|null} info userInfo
     */
    function setUserInfo(info) {
        if (info) {
            userInfo.value = info
            localStorage.setItem("userInfo", JSON.stringify(info))
            return
        }
        userInfo.value = null
        localStorage.removeItem("userInfo")
    }

    /**
     * 存取用戶 permission，附帶處理 localStorage
     * @param {object|null} permissions
     */
    function setUserPermissions(permissions) {
        if (permissions) {
            userPermissions.value = permissions
            localStorage.setItem("userPermissions", JSON.stringify(permissions))
            return
        }
        userPermissions.value = null
        localStorage.removeItem("userPermissions")
    }

    /**
     * 存取用戶 token，附帶處理 localStorage
     * @param {object|null} token
     */
    function setUserToken(token) {
        if (token) {
            reset401Flag()
            userToken.value = token
            localStorage.setItem("userToken", JSON.stringify(token))
            return
        }
        userToken.value = null
        localStorage.removeItem("userToken")
    }

    /**
     * 重置所有 store 狀態至初始值，登出時呼叫
     * 新增 store 時需在此補上對應的 reset() 呼叫
     */
    function resetAllStores() {
        reset()
        useJourneyStore().reset()
    }

    /**
     * 登出：清除 token、導回登入頁、清除用戶資料並重置所有 store
     * @returns {Promise<void>}
     */
    async function logout() {
        setUserToken(null)
        await router.push({ name: "login" })
        setUserInfo(null)
        setUserPermissions(null)
        resetAllStores()
    }

    /**
     * 重置所有狀態至初始值
     */
    function reset() {
        userInfo.value = null
        userPermissions.value = null
        userToken.value = null
    }

    return {
        userInfo,
        userPermissions,
        userToken,
        formatUserPermissions,
        formatUserInfo,
        formatUserToken,
        formatUserRefreshToken,
        setUserInfo,
        setUserPermissions,
        setUserToken,
        logout,
        reset,
    }
})
